const ATTRIBUTION_KEY = 'pyaar_first_touch_v1';
const SESSION_ID_KEY = 'pyaar_session_id_v1';
const SESSION_STARTED_AT_KEY = 'pyaar_session_started_at_v1';
const ANON_DISTINCT_ID_KEY = 'pyaar_distinct_id_v1';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const safeStorage = {
  get(storage, key) {
    try {
      return storage.getItem(key);
    } catch (_error) {
      return null;
    }
  },
  set(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (_error) {
      // no-op for private mode / disabled storage
    }
  },
};

const randomId = () => Math.random().toString(36).slice(2, 12);

const parseUtmParams = search => {
  const params = new URLSearchParams(search || '');
  return UTM_KEYS.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
};

const buildAttributionSnapshot = () => {
  if (typeof window === 'undefined') return {};

  const utm = parseUtmParams(window.location.search);
  const referrer = document.referrer || '';
  const landingPath = window.location.pathname || '/';

  const source =
    utm.utm_source ||
    (referrer ? 'referral' : 'direct');

  return {
    ...utm,
    source,
    referrer,
    landing_path: landingPath,
  };
};

export const initAnalytics = () => {
  // No client SDK required. Events are sent directly to PostHog capture API.
};

export const getFirstTouchAttribution = () => {
  if (typeof window === 'undefined') return {};

  const existing = safeStorage.get(window.localStorage, ATTRIBUTION_KEY);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch (_error) {
      // fall through and overwrite invalid payload
    }
  }

  const snapshot = buildAttributionSnapshot();
  safeStorage.set(window.localStorage, ATTRIBUTION_KEY, JSON.stringify(snapshot));
  return snapshot;
};

export const getSessionContext = () => {
  if (typeof window === 'undefined') return {};

  let sessionId = safeStorage.get(window.sessionStorage, SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = randomId();
    safeStorage.set(window.sessionStorage, SESSION_ID_KEY, sessionId);
  }

  let startedAt = safeStorage.get(window.sessionStorage, SESSION_STARTED_AT_KEY);
  if (!startedAt) {
    startedAt = String(Date.now());
    safeStorage.set(window.sessionStorage, SESSION_STARTED_AT_KEY, startedAt);
  }

  return {
    session_id: sessionId,
    session_started_at_ms: Number(startedAt),
  };
};

const getDistinctId = () => {
  if (typeof window === 'undefined') return '';

  let distinctId = safeStorage.get(window.localStorage, ANON_DISTINCT_ID_KEY);
  if (!distinctId) {
    distinctId = `anon_${randomId()}${randomId()}`;
    safeStorage.set(window.localStorage, ANON_DISTINCT_ID_KEY, distinctId);
  }
  return distinctId;
};

const posthogHost = () => process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const posthogCaptureUrl = () =>
  process.env.REACT_APP_POSTHOG_CAPTURE_URL ||
  `${posthogHost().replace(/\/$/, '')}/capture/`;

const sendToPostHog = (event, properties) => {
  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  if (!apiKey || typeof window === 'undefined') return;

  const payload = {
    api_key: apiKey,
    event,
    properties: {
      ...properties,
      distinct_id: getDistinctId(),
      $current_url: window.location.href,
      $host: window.location.host,
      $pathname: window.location.pathname,
      $lib: 'pyaar-web-direct',
    },
    timestamp: new Date().toISOString(),
  };

  const captureUrl = posthogCaptureUrl();
  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(captureUrl, blob);
    return;
  }

  fetch(captureUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // no-op: analytics should never break product flow
  });
};

export const trackEvent = (name, props = {}) => {
  if (typeof window === 'undefined') return;
  if (!process.env.REACT_APP_POSTHOG_KEY) return;

  const attribution = getFirstTouchAttribution();
  const session = getSessionContext();
  const payload = { ...attribution, ...session, ...props };
  sendToPostHog(name, payload);
};
